import { zodResolver } from "@hookform/resolvers/zod";
import { DismissibleAlert } from "components/DismissibleAlert";
import { FormGroupUI } from "components/FormGroup";
import { FormGroupLabelUI } from "components/FormGroupLabel";
import { RhfInput } from "components/rhfs/RhfInput";
import { RhfRadioGroup } from "components/rhfs/RhfRadioGroup";
import { RhfSelect } from "components/rhfs/RhfSelect";
import { RhfTextarea } from "components/rhfs/RhfTextarea";
import { SenderEmailWarning } from "components/SenderEmailWarning";
import { Button } from "components/ui/Button";
import { DateTimeInput } from "components/ui/DateTimeInput";
import { Loading } from "components/ui/Loading/Loading";
import dayjs from "dayjs";
import { useSenderEmails } from "hooks/use-sender-emails-api";
import {
  type RecipientPreviewItem,
  useCompanyGroupMutations,
  useCompanyGroupsApi,
} from "hooks/useCompanyGroupsApi";
import {
  DeliveryTiming,
  type EmailTemplate,
  useEmailTemplatesRestApi,
} from "hooks/useEmailTemplatesRestApi";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  FormProvider,
  useController,
  useForm,
  useWatch,
} from "react-hook-form";
import { EmailTemplateModal } from "routes/DeliveryPage/components/EmailTemplateModal";
import type { OptionItem } from "types/common";
import { z } from "zod";

export type DeliveryFormValues = {
  distributionGroupId: string;
  includeSender: boolean;
  from: string;
  subject: string;
  body: string;
  deliveryTiming: DeliveryTiming;
  scheduledAt: string;
};

/**
 * 予約配信日時の入力フィールド。30分単位に丸めて設定する。
 * @return {JSX.Element} DateTime 入力
 */
const ScheduledAtField: FC = () => {
  const { field, fieldState } = useController<DeliveryFormValues>({
    name: "scheduledAt",
  });
  const now = dayjs();
  const twoWeeksLater = now.add(2, "week");
  return (
    <div className="flex flex-col">
      <DateTimeInput
        min={now.format("YYYY-MM-DDTHH:mm")}
        max={twoWeeksLater.format("YYYY-MM-DDTHH:mm")}
        value={(field.value as string | undefined) ?? ""}
        onChange={event => {
          const dateTime = dayjs(event.target.value);
          const roundedMinutes = Math.round(dateTime.minute() / 30) * 30;
          field.onChange(
            dateTime
              .minute(roundedMinutes)
              .second(0)
              .format("YYYY-MM-DDTHH:mm"),
          );
        }}
        onBlur={field.onBlur}
        ref={field.ref}
      />
      {fieldState.error && (
        <span className="text-error text-xs">
          {String(fieldState.error.message ?? "")}
        </span>
      )}
    </div>
  );
};

const deliveryFormSchema = z
  .object({
    distributionGroupId: z.string().min(1, "配信先グループを選択してください"),
    includeSender: z.boolean(),
    from: z.string().min(1, "送信者を選択してください"),
    subject: z.string().trim().min(1, "件名を入力してください"),
    body: z.string().trim().min(1, "本文を入力してください"),
    deliveryTiming: z.enum([DeliveryTiming.Now, DeliveryTiming.Scheduled]),
    scheduledAt: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.deliveryTiming === DeliveryTiming.Scheduled && !data.scheduledAt) {
      ctx.addIssue({
        code: "custom",
        message: "予約日時を指定してください",
        path: ["scheduledAt"],
      });
    }
  });

const DELIVERY_TIMING_OPTIONS: OptionItem[] = [
  { label: "今すぐ配信", value: DeliveryTiming.Now },
  { label: "予約配信", value: DeliveryTiming.Scheduled },
];

type Props = {
  initialValues?: Partial<DeliveryFormValues>;
  saveDraftLabel: string;
  saveLoading: boolean;
  sendLoading: boolean;
  onSaveDraft: (values: DeliveryFormValues) => Promise<void>;
  onSend: (values: DeliveryFormValues) => Promise<void>;
};

/**
 * メール配信フォーム（新規・編集共通）
 * @param {Props} props コンポーネントProps
 * @return {JSX.Element} メール配信フォーム
 */
export const DeliveryForm: FC<Props> = ({
  initialValues,
  saveDraftLabel,
  saveLoading,
  sendLoading,
  onSaveDraft,
  onSend,
}) => {
  const { data: groupsData } = useCompanyGroupsApi();
  const {
    data: templatesData,
    refetch: refetchTemplates,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    createLoading: createTemplateLoading,
    updateLoading: updateTemplateLoading,
    deleteLoading: deleteTemplateLoading,
  } = useEmailTemplatesRestApi();
  const { recipientsPreview } = useCompanyGroupMutations();
  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });

  const methods = useForm<DeliveryFormValues>({
    resolver: zodResolver(deliveryFormSchema),
    defaultValues: {
      distributionGroupId: "",
      includeSender: false,
      from: "",
      subject: "",
      body: "",
      deliveryTiming: DeliveryTiming.Now,
      scheduledAt: "",
      ...initialValues,
    },
  });

  const groupId = useWatch({
    control: methods.control,
    name: "distributionGroupId",
  });
  const fromEmail = useWatch({ control: methods.control, name: "from" });
  const timing = useWatch({
    control: methods.control,
    name: "deliveryTiming",
  });

  const [templateId, setTemplateId] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewItems, setPreviewItems] = useState<RecipientPreviewItem[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewOptedOutCount, setPreviewOptedOutCount] = useState(0);
  const [previewCompanyCount, setPreviewCompanyCount] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const groups = groupsData?.items || [];
  const templates = templatesData?.emailTemplates?.items || [];

  const groupOptions = useMemo(
    () =>
      groups.map(g => ({
        label: `${g.name} (${g.companyCount}社)`,
        value: g.id,
      })),
    [groups],
  );

  const senderEmails = senderEmailsData?.items ?? [];

  const fromOptions: OptionItem[] = useMemo(
    () =>
      senderEmails.map(sender => ({
        label: `${sender.displayName} (${sender.email})`,
        value: sender.email,
      })),
    [senderEmails],
  );

  const emailToNameMap = useMemo(
    () =>
      senderEmails.reduce(
        (acc, sender) => {
          acc[sender.email] = sender.displayName;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [senderEmails],
  );

  const fromName = emailToNameMap[fromEmail || ""] || "";

  const fetchPreview = useCallback(
    async (targetGroupId: string) => {
      if (!targetGroupId) {
        setPreviewItems([]);
        setPreviewTotal(0);
        setPreviewOptedOutCount(0);
        setPreviewCompanyCount(0);
        return;
      }

      setPreviewLoading(true);
      try {
        const result = await recipientsPreview(targetGroupId);
        setPreviewItems(result.contacts);
        setPreviewTotal(result.activeContactCount);
        setPreviewOptedOutCount(result.optedOutCount);
        setPreviewCompanyCount(result.companyCount);
      } catch {
        setPreviewItems([]);
        setPreviewTotal(0);
        setPreviewOptedOutCount(0);
        setPreviewCompanyCount(0);
      } finally {
        setPreviewLoading(false);
      }
    },
    [recipientsPreview],
  );

  useEffect(() => {
    if (groupId) {
      fetchPreview(groupId);
      return;
    }
    setPreviewItems([]);
    setPreviewTotal(0);
    setPreviewOptedOutCount(0);
    setPreviewCompanyCount(0);
  }, [groupId, fetchPreview]);

  useEffect(() => {
    if (formError) {
      document
        .getElementById("layout-content-scroll")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [formError]);

  const applyTemplateToForm = useCallback(
    (tmpl: EmailTemplate) => {
      setTemplateId(tmpl.id || "");
      methods.setValue("distributionGroupId", tmpl.distributionGroupId || "");
      methods.setValue("includeSender", !!tmpl.includeSender);
      methods.setValue("from", tmpl.from || "");
      methods.setValue("subject", tmpl.subject || "");
      methods.setValue("body", tmpl.body || "");
      methods.setValue(
        "deliveryTiming",
        tmpl.deliveryTiming || DeliveryTiming.Now,
      );
      methods.setValue("scheduledAt", "");
    },
    [methods],
  );

  const handleTemplateSelect = useCallback(
    (tmpl: EmailTemplate) => {
      applyTemplateToForm(tmpl);
      setShowTemplateModal(false);
    },
    [applyTemplateToForm],
  );

  const handleSaveDraft = useCallback(async () => {
    await methods.handleSubmit(async values => {
      setFormError(null);
      try {
        await onSaveDraft(values);
      } catch (e) {
        setFormError(e instanceof Error ? e.message : "保存に失敗しました");
      }
    })();
  }, [methods, onSaveDraft]);

  const handleOpenConfirm = useCallback(async () => {
    await methods.handleSubmit(() => {
      setFormError(null);
      setShowConfirm(true);
    })();
  }, [methods]);

  const handleSend = async () => {
    setFormError(null);
    try {
      await onSend(methods.getValues());
      setShowConfirm(false);
    } catch (e) {
      setShowConfirm(false);
      setFormError(e instanceof Error ? e.message : "配信に失敗しました");
    }
  };

  const confirmValues = methods.getValues();

  return (
    <>
      {formError && (
        <div className="mb-4">
          <DismissibleAlert
            variant="error"
            onDismiss={() => setFormError(null)}
          >
            {formError}
          </DismissibleAlert>
        </div>
      )}

      <FormProvider {...methods}>
        <div className="card bg-base-100 shadow-md">
          <div className="card-body space-y-4">
            {/* 配信先グループ */}
            <FormGroupUI className="!space-x-0">
              <FormGroupLabelUI className="w-auto mr-0">
                配信先グループ <span className="text-error">*</span>
              </FormGroupLabelUI>
              <RhfSelect
                name="distributionGroupId"
                options={groupOptions}
                placeholder="グループを選択..."
              />
              {groupId && (
                <div className="text-sm mt-1 text-base-content/60">
                  {previewLoading ? (
                    <Loading size="xs" />
                  ) : (
                    `有効な宛先: ${previewTotal}件 (${previewCompanyCount}社${previewOptedOutCount > 0 ? ` / opt-out ${previewOptedOutCount}件` : ""})`
                  )}
                </div>
              )}
            </FormGroupUI>

            {/* 自分にも送信 */}
            <FormGroupUI className="!space-x-0">
              <div className="form-control w-full max-w-xl">
                <label className="label cursor-pointer justify-start gap-4">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary"
                    {...methods.register("includeSender")}
                  />
                  <span className="label-text">自分にも送信する</span>
                </label>
              </div>
            </FormGroupUI>

            {/* メールテンプレート */}
            <div className="form-control">
              <div className="flex justify-end border-t border-gray-100 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTemplateModal(true)}
                >
                  メールテンプレート
                </Button>
              </div>
              {templateId && (
                <div className="text-sm mt-2 text-base-content/60 text-right">
                  選択中:{" "}
                  {templates.find(t => t.id === templateId)?.name || "-"}
                </div>
              )}
            </div>

            {/* 送信者 */}
            <FormGroupUI className="!space-x-0">
              <FormGroupLabelUI className="w-auto mr-0">
                送信者 <span className="text-error">*</span>
              </FormGroupLabelUI>
              {senderEmails.length === 0 ? (
                <SenderEmailWarning className="mt-2" />
              ) : (
                <RhfSelect
                  name="from"
                  options={fromOptions}
                  placeholder="送信者を選択..."
                />
              )}
            </FormGroupUI>

            {/* 件名 */}
            <FormGroupUI className="!space-x-0">
              <FormGroupLabelUI className="w-auto mr-0">
                件名 <span className="text-error">*</span>
              </FormGroupLabelUI>
              <RhfInput
                name="subject"
                maxLength={500}
                placeholder="メールの件名を入力..."
              />
            </FormGroupUI>

            {/* 本文 */}
            <FormGroupUI className="!space-x-0">
              <FormGroupLabelUI className="w-auto mr-0">
                本文 <span className="text-error">*</span>
              </FormGroupLabelUI>
              <RhfTextarea
                name="body"
                rows={8}
                placeholder="メールの本文を入力..."
              />
              <div className="text-xs mt-1 text-base-content/60">
                変数: {"{{COMPANY}}"} = 会社名, {"{{NAME}}"} = 担当者名
              </div>
            </FormGroupUI>

            {/* 配信タイミング */}
            <FormGroupUI className="!space-x-0">
              <FormGroupLabelUI className="w-auto mr-0">
                配信タイミング
              </FormGroupLabelUI>
              <div className="space-y-2">
                <RhfRadioGroup
                  name="deliveryTiming"
                  options={DELIVERY_TIMING_OPTIONS}
                />
                {timing === DeliveryTiming.Scheduled && (
                  <div className="ml-1">
                    <ScheduledAtField />
                  </div>
                )}
              </div>
            </FormGroupUI>
          </div>

          {/* Actions */}
          <div className="card-body pt-0 flex-row justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSaveDraft}
              disabled={saveLoading}
            >
              {saveLoading ? <Loading size="sm" /> : saveDraftLabel}
            </Button>
            <Button
              type="button"
              onClick={handleOpenConfirm}
              disabled={saveLoading || sendLoading}
            >
              確認画面へ
            </Button>
          </div>
        </div>
      </FormProvider>

      {/* 宛先プレビュー */}
      {groupId && previewItems.length > 0 && (
        <div className="card bg-base-100 shadow-md mt-4">
          <div className="card-body">
            <h3 className="card-title text-base">
              宛先プレビュー ({previewTotal}件)
            </h3>
            <div className="overflow-x-auto max-h-60 overflow-y-auto">
              <table className="table table-xs">
                <thead>
                  <tr>
                    <th>会社名</th>
                    <th>担当者</th>
                    <th>メール</th>
                  </tr>
                </thead>
                <tbody>
                  {previewItems.map(r => (
                    <tr key={r.id}>
                      <td>{r.companyName}</td>
                      <td>{r.displayName || "-"}</td>
                      <td className="text-xs">{r.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* メールテンプレート選択・編集モーダル */}
      <EmailTemplateModal
        isOpen={showTemplateModal}
        templates={templates}
        companyGroups={groups}
        onClose={() => setShowTemplateModal(false)}
        onSelectTemplate={handleTemplateSelect}
        selectedTemplateId={templateId}
        onDeleteTemplate={() => setTemplateId("")}
        onTemplatesChanged={refetchTemplates}
        createEmailTemplate={createEmailTemplate}
        updateEmailTemplate={updateEmailTemplate}
        deleteEmailTemplate={deleteEmailTemplate}
        createLoading={createTemplateLoading}
        updateLoading={updateTemplateLoading}
        deleteLoading={deleteTemplateLoading}
      />

      {/* 確認モーダル */}
      {showConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">配信内容確認</h3>
            <div className="py-4 space-y-2">
              <div>
                <span className="font-medium">配信先: </span>
                {groups.find(g => g.id === confirmValues.distributionGroupId)
                  ?.name || "-"}{" "}
                ({previewTotal}件 / {previewCompanyCount}社
                {previewOptedOutCount > 0
                  ? ` / opt-out ${previewOptedOutCount}件`
                  : ""}
                )
              </div>
              <div>
                <span className="font-medium">送信者: </span>
                {fromName || confirmValues.from}
              </div>
              <div>
                <span className="font-medium">件名: </span>
                {confirmValues.subject}
              </div>
              <div>
                <span className="font-medium">タイミング: </span>
                {confirmValues.deliveryTiming === DeliveryTiming.Now
                  ? "今すぐ配信"
                  : `予約: ${confirmValues.scheduledAt}`}
              </div>
              <div className="divider" />
              <div className="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {confirmValues.body}
              </div>
              {confirmValues.deliveryTiming === DeliveryTiming.Now && (
                <div className="alert alert-warning text-sm">
                  <span>
                    {previewTotal}
                    件の宛先に配信されます。この操作は取り消せません。
                  </span>
                </div>
              )}
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowConfirm(false)}
                disabled={saveLoading || sendLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                onClick={handleSend}
                disabled={saveLoading || sendLoading}
              >
                {saveLoading || sendLoading ? (
                  <Loading size="sm" />
                ) : confirmValues.deliveryTiming === DeliveryTiming.Now ? (
                  "配信する"
                ) : (
                  "予約する"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() =>
              !(saveLoading || sendLoading) && setShowConfirm(false)
            }
          />
        </div>
      )}
    </>
  );
};
